import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import { getCountryCallingCode } from '../utils/geoUtils';
import { trackClick } from '../utils/analytics';

const { FiTarget, FiShare2, FiX, FiCheck, FiCopy, FiMessageCircle, FiTwitter, FiMapPin, FiPhone, FiChevronRight, FiUser, FiMap } = FiIcons;
const { FaFacebookMessenger, FaWaze, FaGoogle, FaApple } = FaIcons;

const LocationCard = ({ location, onDetect, loading, address, city, countryCode }) => {
  const [showShare, setShowShare] = useState(false);
  const [shareStep, setShareStep] = useState('select');
  const [selectedMessage, setSelectedMessage] = useState('');
  const [copyStatus, setCopyStatus] = useState(false);

  const googleMapsUrl = location ? `https://www.google.com/maps?q=${location.lat},${location.lng}` : '';
  const wazeUrl = location ? `https://www.waze.com/ul?ll=${location.lat},${location.lng}&navigate=yes` : '';
  const appleMapsUrl = location ? `https://maps.apple.com/?daddr=${location.lat},${location.lng}&dirflg=d` : '';
  const siteUrl = 'https://NearMe.ph';
  const flagUrl = countryCode ? `https://flagcdn.com/w80/${countryCode.toLowerCase()}.png` : null;
  const callingCode = countryCode ? getCountryCallingCode(countryCode) : null;

  const generateMessage = (type) => {
    const street = address || 'my current spot';
    const dialCode = callingCode ? `\n📞 Dial Code: +${callingCode}` : '';
    const navLinks = `🚗 Navigation Links:\n\nGoogle Maps: ${googleMapsUrl}\n\nWaze: ${wazeUrl}\n\nApple Maps: ${appleMapsUrl}`;
    const promo = `✨ Find essentials near you with NearMe Search: ${siteUrl}`;

    let msg = '';
    let btnName = '';
    if (type === 'here') {
      msg = `I am currently located at: ${street}, ${countryCode}${dialCode}\n\n${navLinks}\n\n${promo}`;
      btnName = "Select Template: I'm Here";
    } else if (type === 'pickup') {
      msg = `I am currently located near: ${street}, ${city || ''}, ${countryCode}\n\nI am currently located at: ${street}, ${countryCode}${dialCode}\n\n${navLinks}\n\n${promo}`;
      btnName = "Select Template: Pick Me Up";
    } else if (type === 'meet') {
      msg = `I am currently located near: ${street}, ${city || ''}, ${countryCode}\n\nI am currently located at: ${street}, ${countryCode}${dialCode}\n\n${navLinks}\n\n${promo}`;
      btnName = "Select Template: Let's Meet";
    }

    trackClick(btnName, 'LocationSharing');
    setSelectedMessage(msg);
    setShareStep('options');
  };

  const copyToClipboard = () => {
    trackClick('Copy Location Link', 'ShareOptions');
    navigator.clipboard.writeText(selectedMessage);
    setCopyStatus(true);
    setTimeout(() => setCopyStatus(false), 2000);
  };

  const handleMessengerShare = () => {
    trackClick('Share to Facebook Messenger', 'ShareOptions');
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const encodedUrl = encodeURIComponent(googleMapsUrl); 
    if (isMobile) {
      window.open(`fb-messenger://share?link=${encodedUrl}`, '_blank');
    } else {
      window.open(`https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=291494419107518&redirect_uri=${encodedUrl}`, '_blank');
    }
  };

  return (
    <div className="bg-white rounded-[2.5rem] sm:rounded-[3rem] p-6 sm:p-8 high-contrast-shadow mb-8 sm:mb-10 border border-slate-200">
      <button 
        onClick={onDetect} 
        disabled={loading}
        className="w-full bg-indigo-600 text-white font-black py-4 sm:py-5 rounded-2xl flex items-center justify-center gap-3 hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-70 mb-6 shadow-xl shadow-indigo-100"
      >
        <SafeIcon icon={FiTarget} className={`text-xl ${loading ? 'animate-spin' : ''}`} />
        <span className="text-base sm:text-lg tracking-tight">{loading ? 'Acquiring...' : 'Detect My Location'}</span>
      </button>

      {location && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <div className="p-5 sm:p-6 bg-slate-50 rounded-[2rem] sm:rounded-[2.5rem] border border-slate-100">
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="flex-shrink-0">
                {flagUrl ? (
                  <img src={flagUrl} alt={countryCode} className="w-12 sm:w-14 h-auto rounded-lg shadow-sm border-2 border-white" />
                ) : (
                  <div className="w-12 h-9 bg-slate-200 rounded-lg animate-pulse" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <SafeIcon icon={FiMapPin} className="text-indigo-600 text-[10px]" />
                  <p className="font-black text-indigo-600 text-[9px] uppercase tracking-[0.2em]">
                    Pinned in {city || 'Detecting...'}, {countryCode}
                  </p>
                </div>
                <h2 className="text-slate-950 font-black text-base sm:text-lg leading-tight tracking-tight break-words">
                  {address || 'Location Identified'}
                </h2>
              </div>
            </div>
          </div>

          <button 
            onClick={() => { trackClick('Open Share Modal', 'LocationCard'); setShowShare(true); }}
            className="w-full bg-slate-950 text-white font-black py-4 sm:py-5 rounded-2xl flex items-center justify-center gap-2 hover:bg-black transition-all active:scale-[0.98] shadow-lg shadow-slate-200"
          >
            <SafeIcon icon={FiShare2} className="text-lg" />
            <span className="text-[10px] uppercase tracking-[0.2em]">Share My Location</span>
          </button>
        </motion.div>
      )}

      <AnimatePresence>
        {showShare && (
          <div className="fixed inset-0 z-[60] flex items-end justify-center px-4 pb-6 sm:items-center sm:pb-0">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowShare(false)} className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" />
            <motion.div 
              initial={{ y: 50, opacity: 0 }} 
              animate={{ y: 0, opacity: 1 }} 
              exit={{ y: 50, opacity: 0 }} 
              className="relative bg-white w-full max-w-sm rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-8 high-contrast-shadow border border-slate-200"
            >
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h4 className="text-xl font-black text-slate-950 tracking-tight">
                    {shareStep === 'select' ? 'Choose Message' : 'Share via...'}
                  </h4>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                    Step {shareStep === 'select' ? '1' : '2'} of 2
                  </p>
                </div>
                <button onClick={() => setShowShare(false)} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors">
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              {shareStep === 'select' ? (
                <div className="space-y-4">
                  <button onClick={() => generateMessage('here')} className="w-full flex items-center justify-between p-5 bg-blue-50 border-2 border-blue-100 rounded-2xl hover:border-blue-400 transition-all group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-blue-600 shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-all">
                        <SafeIcon icon={FiMapPin} />
                      </div>
                      <span className="font-black text-slate-950 text-xs uppercase tracking-widest">I’m here:</span>
                    </div>
                    <SafeIcon icon={FiChevronRight} className="text-blue-300" />
                  </button>

                  <button onClick={() => generateMessage('pickup')} className="w-full flex items-center justify-between p-5 bg-emerald-50 border-2 border-emerald-100 rounded-2xl hover:border-emerald-400 transition-all group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-emerald-600 shadow-sm group-hover:bg-emerald-600 group-hover:text-white transition-all">
                        <SafeIcon icon={FiUser} />
                      </div>
                      <span className="font-black text-slate-950 text-xs uppercase tracking-widest">Pick Me Up Here</span>
                    </div>
                    <SafeIcon icon={FiChevronRight} className="text-emerald-300" />
                  </button>

                  <button onClick={() => generateMessage('meet')} className="w-full flex items-center justify-between p-5 bg-orange-50 border-2 border-orange-100 rounded-2xl hover:border-orange-400 transition-all group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-orange-600 shadow-sm group-hover:bg-orange-600 group-hover:text-white transition-all">
                        <SafeIcon icon={FiMap} />
                      </div>
                      <span className="font-black text-slate-950 text-xs uppercase tracking-widest">Let’s Meet Here</span>
                    </div>
                    <SafeIcon icon={FiChevronRight} className="text-orange-300" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-4 gap-3 mb-6">
                    {[
                      { icon: FiCopy, color: 'bg-indigo-600', name: 'Copy', action: copyToClipboard },
                      { icon: FiMessageCircle, color: 'bg-emerald-500', name: 'SMS', url: `sms:?body=${encodeURIComponent(selectedMessage)}` },
                      { icon: FiTwitter, color: 'bg-slate-900', name: 'X', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(selectedMessage)}` },
                      { icon: FaFacebookMessenger, color: 'bg-blue-500', name: 'Msgr', action: handleMessengerShare }
                    ].map((social, i) => (
                      <button 
                        key={i} 
                        onClick={() => {
                          if (social.action) social.action();
                          else {
                            trackClick(`Share via ${social.name}`, 'ShareOptions');
                            window.open(social.url, '_blank');
                          }
                        }}
                        className="flex flex-col items-center gap-2"
                      >
                        <div className={`${social.color} p-3 rounded-xl text-white shadow-lg active:scale-90 transition-transform w-full aspect-square flex items-center justify-center`}>
                          <SafeIcon icon={social.icon} className="text-lg" />
                        </div>
                        <span className="text-[8px] font-black text-slate-700 uppercase tracking-widest">{social.name}</span>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setShareStep('select')} className="w-full text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-950">
                    ← Back to Templates
                  </button>
                </>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LocationCard;